import type { SxProps } from '@mui/material'
import type { MenuProps } from '@mui/material'
import type { ThemedStyle } from '../types'
import { zIndex } from './utilities'

/**
 * Dialog styles and overlay menu helpers.
 *
 * Stacking lives in `zIndex` (utilities.ts). Menu/Autocomplete z-index is also
 * applied globally via ThemedMuiProvider — use these helpers in dialogs for
 * disableScrollLock and explicit elevation.
 */

const floatingMenuZ = zIndex.floatingMenu

function buildMenuProps(options?: {
  maxHeight?: number
  passThroughBackdrop?: boolean
  menuZIndex?: number
}): Partial<MenuProps> {
  const menuZ = options?.menuZIndex ?? floatingMenuZ
  return {
    disablePortal: false,
    disableScrollLock: true,
    style: { zIndex: menuZ },
    PaperProps: {
      sx: {
        zIndex: menuZ,
        ...(options?.maxHeight != null ? { maxHeight: options.maxHeight } : {}),
      },
    },
    ...(options?.passThroughBackdrop
      ? { BackdropProps: { sx: { pointerEvents: 'none' } } }
      : {}),
  }
}

const floatingPopperProps = {
  style: { zIndex: floatingMenuZ },
} as const

/**
 * Standard dialog container style with z-index
 * Used for MUI Dialog components
 */
export const $sxDialog: ThemedStyle<SxProps> = () => ({
  zIndex: zIndex.modal,
})

/**
 * Standard dialog backdrop style with z-index
 * Used for MUI Dialog backdrop
 */
export const $sxDialogBackdrop: ThemedStyle<SxProps> = () => ({
  // Ensure the backdrop sits behind the dialog content within the modal
  // so it does not block interaction with the dialog itself.
  // MUI's default for a modal backdrop is effectively "behind" the content,
  // which we replicate here with a negative z-index.
  zIndex: -1,
})

/**
 * Standard dialog paper style with z-index and border radius
 * Used for MUI Dialog PaperProps
 */
export const $sxDialogPaper: ThemedStyle<SxProps> = ({ spacing }) => ({
  zIndex: zIndex.modalContent,
  borderRadius: `${spacing.sm}px`,
})

/**
 * Menu props for Select/Menu dropdowns inside standard app dialogs.
 * Also use for elevated-editor dialogs — z-index clears every dialog layer.
 */
export const $dialogMenuProps: Partial<MenuProps> = buildMenuProps()

/**
 * Same as $dialogMenuProps, but the menu backdrop does not capture clicks.
 * Prefer this when a dialog previously relied on BackdropProps pointerEvents: 'none'.
 */
export const $dialogMenuPropsPassThrough: Partial<MenuProps> = buildMenuProps({
  passThroughBackdrop: true,
})

/**
 * Popper props for Autocomplete dropdowns inside app dialogs / overlays.
 */
export const dialogAutocompletePopperProps = floatingPopperProps

/**
 * Menu props for Select dropdowns inside PortalPopup overlays.
 * Adds maxHeight and disableScrollLock (avoids editor panel shift).
 */
export const $portalPopupSelectMenuProps: Partial<MenuProps> = buildMenuProps({
  maxHeight: 300,
})

/** Z-index for MUI Dialogs that must appear above PortalPopup (studio, editors). */
export const PORTAL_POPUP_DIALOG_Z_INDEX = zIndex.portalPopupDialog

/**
 * Dialog container styles for use inside PortalPopup overlays.
 * PortalPopup defaults to z-index 10002; MUI Dialog defaults to 1300 without this.
 */
export const $sxPortalPopupDialog: ThemedStyle<SxProps> = () => ({
  zIndex: PORTAL_POPUP_DIALOG_Z_INDEX,
  '& .MuiDialog-container': {
    zIndex: PORTAL_POPUP_DIALOG_Z_INDEX,
  },
  '& .MuiBackdrop-root': {
    zIndex: PORTAL_POPUP_DIALOG_Z_INDEX - 1,
  },
})

export const portalPopupDialogSlotProps = {
  backdrop: {
    sx: { zIndex: PORTAL_POPUP_DIALOG_Z_INDEX - 1 },
  },
}

export const $sxPortalPopupDialogPaper: ThemedStyle<SxProps> = ({ spacing }) => ({
  zIndex: PORTAL_POPUP_DIALOG_Z_INDEX + 1,
  position: 'relative',
  borderRadius: `${spacing.sm}px`,
})

/** Select menus inside a portal-popup dialog (same floating layer as other menus). */
export const portalPopupDialogMenuProps: Partial<MenuProps> = buildMenuProps({
  maxHeight: 300,
})

/** Autocomplete poppers inside a portal-popup dialog. */
export const portalPopupDialogAutocompletePopperProps = floatingPopperProps

/**
 * Dialogs opened over the email/GrapeJS editor (save template, import content).
 * Prefer component-scoped elevation over global `.MuiBackdrop-root` CSS — that
 * broke AdminNav Drawer clicks on Communications once GrapeJS CSS loaded.
 */
export const ELEVATED_EDITOR_DIALOG_Z_INDEX = zIndex.elevatedEditorDialog

export const $sxElevatedEditorDialog: ThemedStyle<SxProps> = () => ({
  zIndex: ELEVATED_EDITOR_DIALOG_Z_INDEX,
  '& .MuiDialog-container': {
    zIndex: ELEVATED_EDITOR_DIALOG_Z_INDEX,
  },
  '& .MuiBackdrop-root': {
    zIndex: ELEVATED_EDITOR_DIALOG_Z_INDEX - 1,
  },
})

export const elevatedEditorDialogSlotProps = {
  backdrop: {
    sx: { zIndex: ELEVATED_EDITOR_DIALOG_Z_INDEX - 1 },
  },
} as const

/** Select menus inside elevated-editor dialogs — alias of `$dialogMenuProps`. */
export const $elevatedEditorMenuProps: Partial<MenuProps> = $dialogMenuProps

/**
 * Select menus opened from inside the mobile admin nav drawer.
 * Must sit above `zIndex.adminNavDrawer` or the list renders behind the sidebar.
 */
export const $adminNavDrawerMenuProps: Partial<MenuProps> = buildMenuProps({
  menuZIndex: zIndex.adminNavDrawerMenu,
})
